/**
 * findings-kit — the card grammar both reports speak.
 *
 * Availability supplied the anatomy (severity + category chips, a findings list
 * that drives the evidence below it, recommendations that state impact / action
 * / where); Campus Pulse supplied the interpretive voice and the method line.
 * This module is the merge, so a reader who learns one report can read the
 * other, and neither screen owns a private copy of the chips.
 *
 * A card is: severity + category · one number and one interpreted sentence ·
 * impact / action / owner as labelled rows · one verb · the method underneath.
 *
 * Severity is thresholded on the numbers, never fixed per finding type, with
 * two honesty rules the reports share:
 *   • a card whose inputs the current filter removed is `unreadable`, not zero;
 *   • a sample too small to conclude on caps severity and says so.
 * `clear` is a real severity — a finding with nothing in it must not read as a
 * low-grade problem.
 *
 * The kit deliberately knows nothing about students, events or occurrences: a
 * report builds its own finding list and passes the display model in. That is
 * what keeps one grammar from becoming one report.
 */
import { type ReactNode, type CSSProperties } from "react";
import { Btn, Card, Icon, T } from "@v0y4ge/jrni-ui";

import { useT } from "@v0y4ge/i18n-runtime";
export type Severity = "high" | "medium" | "low" | "clear" | "unreadable";
export type FindingCategory = "outreach" | "schedule" | "staffing" | "curriculum" | "continuity" | "data";

export const SEVERITY_LABEL: Record<Severity, string> = { // ui-config — enum display labels
  high: "High", medium: "Medium", low: "Low", clear: "Clear", unreadable: "Not readable",
};
export const CATEGORY_LABEL: Record<FindingCategory, string> = { // ui-config
  outreach: "Outreach", schedule: "Schedule", staffing: "Staffing",
  curriculum: "Curriculum", continuity: "Continuity", data: "Data quality",
};

/** message = contact each person · activity = write to each record · handoff =
 *  record the hand-off to a team that owns no rows of its own. */
export interface FindingVerb {
  id: string; label: string; kind: "message" | "activity" | "handoff"; done: string;
}

/** Everything the card renders. A report's own finding type extends this with
 *  whatever it needs to act on (its row list, its verb, its gates). */
export interface FindingCardModel {
  id: string; index: string;
  value: string; unit: string; headline: string;
  severity: Severity; note: string | null;
  category: FindingCategory;
  read: string;
  impact: string; action: string; owner: string;
  method: string;
  rows: { k: string; v: string }[];
}

export type VerbState = { state: "done"; n: number; at: number } | undefined;

/* ── Thresholding helpers ─────────────────────────────────────────────── */
export const share = (a: number, b: number) => (b ? a / b : 0);
/** Three thresholds → a severity. `clear` when nothing is there at all. */
export function sev(hi: boolean, mid: boolean, any: boolean): Severity {
  return hi ? "high" : mid ? "medium" : any ? "low" : "clear";
}
/** A high finding on too small a sample is a medium finding with a caveat. */
export function capAt(s: Severity, smallSample: boolean): Severity {
  return s === "high" && smallSample ? "medium" : s;
}

/* ── Chips ────────────────────────────────────────────────────────────── */
const SEV_TONE: Record<Severity, { bg: string; fg: string }> = {
  high: { bg: T.red2, fg: T.red1 },
  medium: { bg: T.orange2, fg: T.orange1 },
  low: { bg: T.n6, fg: T.n2 },
  clear: { bg: T.green2, fg: T.green1 },
  unreadable: { bg: T.white, fg: T.soft },
};
export const SEV_ACCENT: Record<Severity, string> = {
  high: T.red1, medium: T.orange1, low: T.n1, clear: T.green1, unreadable: T.soft,
};
const CAT_TONE: Record<FindingCategory, string> = {
  outreach: T.p1, schedule: T.blue1, staffing: T.orange1,
  curriculum: T.tPurple, continuity: T.tTurquoise, data: T.soft,
};

const MONO: CSSProperties = { fontFamily: T.fontMono, fontVariantNumeric: "tabular-nums" };
const EYEBROW: CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.soft };
const PRETTY = { textWrap: "pretty" } as CSSProperties;

export function SevChip({ s }: { s: Severity }) {
  const tone = SEV_TONE[s];
  return (
    <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg, border: s === "unreadable" ? `1px solid ${T.border}` : "none", whiteSpace: "nowrap" }}>
      {SEVERITY_LABEL[s]}
    </span>
  );
}

export function CatChip({ c }: { c: FindingCategory }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: T.n2, background: T.n6, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: 2, background: CAT_TONE[c] }} />{CATEGORY_LABEL[c]}
    </span>
  );
}

/** IMPACT / ACTION / OWNER — the same three beats on every card in both apps. */
export function Beat({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "62px minmax(0, 1fr)", gap: 9, alignItems: "baseline" }}>
      <span style={{ ...EYEBROW, fontSize: 9 }}>{k}</span>
      <span style={{ fontSize: 11.5, lineHeight: 1.45, color: T.n1, ...PRETTY }}>{children}</span>
    </div>
  );
}

export function FindingCard({ f, verb, filterable, active, onToggle, verbState, onVerb, busy, canAct, actDenyReason, filterLabel }: {
  f: FindingCardModel;
  verb?: FindingVerb | null;
  /** True when selecting this card can scope the evidence below it. */
  filterable?: boolean;
  active?: boolean;
  onToggle?: () => void;
  verbState?: VerbState;
  onVerb?: () => void;
  busy?: boolean;
  canAct?: boolean;
  /** Shown as the disabled verb's tooltip — never a silent dead button. */
  actDenyReason?: string;
  filterLabel?: string;
}) {
  const t = useT();
  return (
    <Card style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 0, minWidth: 0, justifyContent: "space-between", border: `1px solid ${active ? T.p1 : T.border}`, boxShadow: active ? `0 0 0 3px ${T.p4}` : undefined }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ ...MONO, fontSize: 9.5, fontWeight: 700, width: 16, height: 16, borderRadius: 4, background: T.n6, color: T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{f.index}</span>
          <SevChip s={f.severity} />
          <CatChip c={f.category} />
          {active ? <span style={{ ...MONO, marginLeft: "auto", fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.p1 }}>{t("reports.findingsKit.text", "scoping report")}</span> : null}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <span style={{ ...MONO, fontSize: 27, fontWeight: 800, color: SEV_ACCENT[f.severity] }}>{f.value}</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{f.headline}</span>
          <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{f.unit}</span>
        </div>
        <span style={{ fontSize: 12, lineHeight: 1.45, color: T.n2, ...PRETTY }}>{f.read}</span>
        {f.rows.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 9, borderTop: `1px solid ${T.n6}` }}>
            {f.rows.map((r, n) => (
              <div key={n} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11.5 }}>
                <span style={{ color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.k}</span>
                <span style={{ ...MONO, color: T.n1, fontWeight: 600, whiteSpace: "nowrap" }}>{r.v}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 9, borderTop: `1px solid ${T.n6}` }}>
          <Beat k="Impact">{f.impact}</Beat>
          <Beat k="Action">{f.action}</Beat>
          <Beat k="Owner">{f.owner}</Beat>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
          {verb ? (
            verbState ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: T.green1, background: T.green2, borderRadius: 999, padding: "5px 11px" }}>
                <Icon.check size={12} /> {verb.done}{verbState.n ? t("reports.findingsKit.text2", "· {n}", { n: verbState.n }) : ""}
              </span>
            ) : (
              <Btn variant="primary" disabled={!canAct || busy} onClick={onVerb}
                title={canAct ? undefined : (actDenyReason || t("reports.findingsKit.actDenied", "Your role can read this report but not act on its behalf"))}>
                {busy ? t("reports.findingsKit.text3", "Working…") : verb.label}
              </Btn>
            )
          ) : null}
          {filterable && onToggle ? (
            <Btn variant={active ? "primary" : "secondary"} onClick={onToggle}>
              {active ? t("reports.findingsKit.text4", "Clear filter") : (filterLabel || t("reports.findingsKit.filterReport", "Filter report"))}
            </Btn>
          ) : null}
        </div>
        <span style={{ ...MONO, fontSize: 10, lineHeight: 1.4, color: T.soft, ...PRETTY }}>{f.method}</span>
        {f.note ? <span style={{ ...MONO, fontSize: 10, color: T.orange1 }}>{f.note}</span> : null}
      </div>
    </Card>
  );
}

/** The band header both reports use above their card grid. */
export function FindingsHeader({ n, note, open, onToggle, right }: {
  n: number; note: string; open: boolean; onToggle: () => void; right?: ReactNode;
}) {
  const t = useT();
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", minWidth: 0 }}>
        <button onClick={onToggle} title={open ? t("reports.findingsKit.title", "Minimize findings") : t("reports.findingsKit.title2", "Show findings")}
          style={{ display: "flex", alignItems: "center", gap: 6, border: 0, background: "transparent", padding: "2px 4px 2px 0", margin: 0, cursor: "pointer", fontFamily: "inherit", color: T.soft }}>
          <span style={{ display: "inline-flex", transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms ease" }}><Icon.chev size={12} /></span>
          <span style={{ ...EYEBROW, color: T.n1 }}>{t("reports.findingsKit.text5", "Findings")}</span>
        </button>
        <span style={{ fontSize: 11, color: T.soft }}>{n} {t("reports.findingsKit.text6", "findings ·")}{note}</span>
      </span>
      {right}
    </div>
  );
}
